// ContactUsForm.js
import { useState, useEffect, useRef } from "react";
import { collection, addDoc, getDocs, serverTimestamp, query } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { useTranslation } from "react-i18next";
import emailjs from "@emailjs/browser";
import { trackFormSubmit } from "../../utils/analytics";
import ReCAPTCHA from "react-google-recaptcha";
import Breadcrumb from "../../components/ui/Breadcrumb";
import SeoMeta from "../../components/ui/SeoMeta";

const PROGRAMS_CACHE_KEY = "ec_programs_cache_v1";
const PROGRAMS_TTL_MS = 5 * 60 * 1000; // 5 phút
const SUBMIT_COOLDOWN_MS = 10 * 1000;  // chặn double-submit 10s

const vnPhoneRegex = /^(?:\+?84|0)(?:[2-9]\d{8}|1\d{9})$/; // cơ bản cho VN; tuỳ biến nếu cần
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ContactUsForm = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "vi";

  const [programs, setPrograms] = useState([]);
  const [progLoading, setProgLoading] = useState(true);

  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    program: "",
    note: "",
    // honeypot
    website: ""
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastSubmitAt, setLastSubmitAt] = useState(0);

  const recaptchaRef = useRef();

  // ===== Load & cache programs =====
  useEffect(() => {
    const cached = sessionStorage.getItem(PROGRAMS_CACHE_KEY);
    if (cached) {
      try {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < PROGRAMS_TTL_MS) {
          setPrograms(data);
          setProgLoading(false);
          return;
        }
      } catch {}
    }

    const fetchPrograms = async () => {
      try {
        // Nếu bạn có field 'visible' + 'order' trong programs thì giữ where/orderBy; nếu không có thì bỏ where/orderBy
        const q = query(
          collection(db, "programs"),
          // where("visible", "==", true), // bật nếu có
          // orderBy("order", "asc")       // bật nếu có
        );
        const snapshot = await getDocs(q);
        const list = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          list.push({
            slug: data.slug,
            level: data.level || {},    // { vi, en }
            visible: data.visible ?? true
          });
        });
        setPrograms(list);
        sessionStorage.setItem(PROGRAMS_CACHE_KEY, JSON.stringify({ data: list, ts: Date.now() }));
      } catch (error) {
        console.error("Error loading programs:", error);
      } finally {
        setProgLoading(false);
      }
    };
    fetchPrograms();
  }, []);

  // ===== Helpers =====
  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    // huỷ lỗi field khi user sửa
    setErrors((e) => ({ ...e, [name]: "" }));
  };

  const sanitize = (obj) => {
    const o = { ...obj };
    for (const k of Object.keys(o)) {
      if (typeof o[k] === "string") o[k] = o[k].trim();
    }
    return o;
  };

  const validate = (f) => {
    const e = {};
    if (!f.fullName) e.fullName = t("register.requiredFields") || "Vui lòng nhập họ tên.";
    if (!f.phone) e.phone = t("register.requiredFields") || "Vui lòng nhập số điện thoại.";
    else if (!vnPhoneRegex.test(f.phone.replace(/\s+/g, ""))) e.phone = t("register.invalidPhone") || "Số điện thoại không hợp lệ (VN).";
    if (!f.program) e.program = t("register.requiredFields") || "Vui lòng chọn chương trình.";
    if (f.email && !emailRegex.test(f.email)) e.email = t("register.invalidEmail") || "Email không hợp lệ.";
    return e;
    // Note: reCAPTCHA validate ở handleSubmit
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  // ===== EmailJS (không chặn flow nếu lỗi) =====
  const sendEmail = async (payload) => {
    try {
      const sid = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const tid = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const pk  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
      if (!sid || !tid || !pk) return;
      await emailjs.send(sid, tid, payload, pk);
    } catch (err) {
      console.error("❌ Email send failed", err);
    }
  };

  // ===== Submit =====
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("");
    setErrors({});

    // Honeypot: nếu 'website' có data -> bot
    if (form.website) {
      setStatus("Your submission was flagged as spam.");
      return;
    }

    // Cooldown double submit
    if (Date.now() - lastSubmitAt < SUBMIT_COOLDOWN_MS) return;

    const f = sanitize(form);
    const eMap = validate(f);
    if (Object.values(eMap).some(Boolean)) {
      setErrors(eMap);
      setStatus(t("register.requiredFields"));
      return;
    }

    if (!recaptchaValue) {
      setStatus(t("register.captchaRequired") || "Vui lòng xác nhận captcha.");
      return;
    }

    try {
      setSubmitting(true);
      // Ghi Firestore
      await addDoc(collection(db, "registrations"), {
        fullName: f.fullName,
        phone: f.phone,
        email: f.email || null,
        program: f.program,
        note: f.note || "",
        lang,
        status: "new",
        createdAt: serverTimestamp(),
        client: {
          ua: typeof navigator !== "undefined" ? navigator.userAgent : null,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
          href: typeof window !== "undefined" ? window.location.href : null
        },
        // Lưu token để có thể verify server-side trong Cloud Functions (khuyến nghị)
        recaptchaToken: recaptchaValue
      });

      // Email thông báo (không block flow)
      await sendEmail({
        fullName: f.fullName,
        phone: f.phone,
        email: f.email || "",
        program: f.program,
        note: f.note || "",
        lang
      });

      trackFormSubmit("Form Đăng ký học thử");

      // Reset
      setShowModal(true);
      setForm({ fullName: "", phone: "", email: "", program: "", note: "", website: "" });
      recaptchaRef.current?.reset();
      setRecaptchaValue(null);
      setStatus("");
      setLastSubmitAt(Date.now());
    } catch (error) {
      console.error("Registration error:", error);
      setStatus(t("register.error") || "Có lỗi xảy ra. Vui lòng thử lại.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-6 px-4 space-y-6">
      <SeoMeta page="contact-page" initialMeta={{ title: `${t("register.heading")}`, description: "" }} />
      <Breadcrumb items={[{ label: t("register.heading") }]} />

      <div className="max-w-2xl mx-auto mt-4 text-center mb-4">
        <div className="flex flex-col items-center gap-2">
          <div className="text-4xl mb-2">📚</div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-primary mb-1">
            {t("register.heading")}
          </h1>
          <div className="text-text2 text-base mb-3">
            {t("register.subheading") ||
              "Điền thông tin để nhận tư vấn và sắp xếp lịch học thử phù hợp nhất cho bạn. Đội ngũ giáo viên sẽ liên hệ ngay trong vòng 24h!"}
          </div>
          <ul className="text-left text-sm text-menu bg-bg/60 rounded-xl p-4 border border-border shadow-sm max-w-md mx-auto">
            <li className="mb-1 flex items-center gap-2"><span className="text-success">✓</span> {t("register.consult1")}</li>
            <li className="mb-1 flex items-center gap-2"><span className="text-success">✓</span> {t("register.consult2")}</li>
            <li className="mb-1 flex items-center gap-2"><span className="text-success">✓</span> {t("register.consult3")}</li>
          </ul>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-card rounded-2xl shadow p-8 max-w-2xl mx-auto mt-6 border border-border">
        {/* Honeypot (ẩn) */}
        <input
          type="text"
          name="website"
          autoComplete="off"
          value={form.website}
          onChange={handleChange}
          tabIndex={-1}
          aria-hidden="true"
          className="hidden"
        />

        {/* Row 1: Full name + Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block font-bold mb-1 text-primary">{t("register.fullName")} *</label>
            <input
              name="fullName"
              value={form.fullName}
              onChange={handleChange}
              className={`w-full p-3 border rounded-2xl text-primary focus:outline-none focus:ring-2 transition
                ${errors.fullName ? "border-red-400 focus:ring-red-200" : "border-border focus:ring-primary"}`}
              required
              autoComplete="name"
              onBlur={(e)=>setField("fullName", e.target.value.trim())}
            />
            {errors.fullName && <p className="text-xs text-red-600 mt-1">{errors.fullName}</p>}
          </div>
          <div>
            <label className="block font-bold mb-1 text-primary">{t("register.phone")} *</label>
            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              className={`w-full p-3 border rounded-2xl text-primary focus:outline-none focus:ring-2 transition
                ${errors.phone ? "border-red-400 focus:ring-red-200" : "border-border focus:ring-primary"}`}
              required
              autoComplete="tel"
              inputMode="tel"
              onBlur={(e)=>setField("phone", e.target.value.trim())}
            />
            {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
          </div>
        </div>

        {/* Email */}
        <div className="mb-6">
          <label className="block font-bold mb-1 text-primary">{t("register.email")}</label>
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            className={`w-full p-3 border rounded-2xl text-primary focus:outline-none focus:ring-2 transition
              ${errors.email ? "border-red-400 focus:ring-red-200" : "border-border focus:ring-primary"}`}
            autoComplete="email"
            onBlur={(e)=>setField("email", e.target.value.trim())}
          />
          {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
        </div>

        {/* Program */}
        <div className="mb-6">
          <label className="block font-bold mb-1 text-primary">{t("register.program")} *</label>
          <select
            name="program"
            value={form.program}
            onChange={handleChange}
            className={`w-full p-3 border rounded-2xl text-primary focus:outline-none focus:ring-2 transition
              ${errors.program ? "border-red-400 focus:ring-red-200" : "border-border focus:ring-primary"}`}
            required
            disabled={progLoading}
          >
            <option value="">{progLoading ? t("loading") || "Đang tải..." : t("register.chooseProgram")}</option>
            {!progLoading && programs.map((p) => {
              const displayName = p.level?.[lang] || p.level?.vi || p.slug;
              return (
                <option key={p.slug} value={p.slug}>
                  {displayName}
                </option>
              );
            })}
          </select>
          {errors.program && <p className="text-xs text-red-600 mt-1">{errors.program}</p>}
        </div>

        {/* Note */}
        <div className="mb-6">
          <label className="block font-bold mb-1 text-primary">{t("register.note")}</label>
          <textarea
            name="note"
            value={form.note}
            onChange={handleChange}
            className="w-full p-3 border border-border rounded-2xl text-primary focus:outline-none focus:ring-2 focus:ring-primary transition"
            rows={2}
            onBlur={(e)=>setField("note", e.target.value.trim())}
          />
        </div>

        {/* reCAPTCHA */}
        <div className="mb-8 flex justify-center">
          <ReCAPTCHA
            sitekey="6LfrUm8rAAAAAEIgNW8Fbam58pVYwLraoVMW4EMm"
            onChange={(value) => setRecaptchaValue(value)}
            onExpired={() => setRecaptchaValue(null)}
            ref={recaptchaRef}
            hl={lang.startsWith("vi") ? "vi" : "en"}
          />
        </div>

        {/* Button group */}
        <div className="flex justify-end items-center space-x-4 border-t border-border pt-6">
          <button
            type="button"
            className="text-menu px-6 py-2 rounded-2xl hover:bg-bg font-semibold transition disabled:opacity-60"
            onClick={() => { setForm({ fullName: "", phone: "", email: "", program: "", note: "", website: "" }); setErrors({}); }}
            disabled={submitting}
          >
            {t("register.cancel") || "Cancel"}
          </button>
          <button
            type="submit"
            className="bg-primary hover:bg-secondary text-textButton font-bold px-8 py-2 rounded-2xl shadow transition text-base disabled:opacity-60"
            disabled={submitting || progLoading}
          >
            {submitting ? (t("register.submitting") || "Đang gửi...") : (t("register.submit") || "Save")}
          </button>
        </div>
        {status && <p className="text-sm mt-3 text-primary">{status}</p>}
      </form>

      {showModal && (
        <div className="fixed inset-0 bg-primary/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card rounded-2xl p-8 shadow-2xl max-w-xs w-full text-center border-2 border-border">
            <h2 className="text-2xl font-extrabold mb-4 text-success">🎉 {t("register.success")}</h2>
            <p className="mb-5 text-primary">{t("register.thankYou")}</p>
            <button
              onClick={() => setShowModal(false)}
              className="mt-2 bg-primary hover:bg-secondary text-textButton font-bold px-8 py-2 rounded-2xl shadow transition"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactUsForm;
